import client from './client';
import { toRawListParams } from './paging';
import { User, PaginatedResponse, ListParams } from '../types/index';

// Backend returns snake_case; the frontend User type is camelCase. Map once here
// so every consumer (list, detail, search) gets a consistent shape and never
// has to guard against `undefined` when calling `.toLowerCase()`/`.split()` etc.
interface RawUser {
  id: string;
  entra_oid: string;
  display_name?: string | null;
  email?: string | null;
  job_title?: string | null;
  department?: string | null;
  profile_photo_url?: string | null;
  entra_profile_url?: string | null;
  is_active?: boolean | null;
  created_at?: string | null;
  updated_at?: string | null;
  last_synced_at?: string | null;
  last_login_at?: string | null;
  ou_memberships?: string[] | null;
}

const mapUser = (u: RawUser): User => ({
  id: u.id,
  entraOid: u.entra_oid,
  displayName: u.display_name ?? '',
  email: u.email ?? '',
  jobTitle: u.job_title ?? undefined,
  department: u.department ?? undefined,
  profilePhotoUrl: u.profile_photo_url ?? undefined,
  entraProfileUrl: u.entra_profile_url ?? undefined,
  isActive: Boolean(u.is_active),
  createdAt: u.created_at ?? '',
  updatedAt: u.updated_at ?? '',
  lastSyncedAt: u.last_synced_at ?? undefined,
  lastLoginAt: u.last_login_at ?? undefined,
  ouMemberships: u.ou_memberships ?? [],
});

interface RawPaginatedUsers {
  items: RawUser[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export const getUsers = async (params?: ListParams): Promise<PaginatedResponse<User>> => {
  // WI #337: `params` used to be forwarded verbatim, so camelCase `pageSize` /
  // `sortBy` never bound to `PaginationParams` and the server quietly returned
  // its default first 20 rows.
  const response = await client.get<RawPaginatedUsers>('/users', {
    params: toRawListParams(params),
  });
  const data = response.data;
  return {
    items: (data.items || []).map(mapUser),
    total: data.total,
    page: data.page,
    pageSize: data.page_size,
    pages: data.total_pages,
  };
};

export const getUser = async (id: string): Promise<User> => {
  const response = await client.get<RawUser>(`/users/${id}`);
  return mapUser(response.data);
};

// Feature #144: roles/memberships view on the user detail page.
export interface UserAccessGroupInfo {
  id: string;
  entraObjectId: string;
  displayName: string;
  description?: string;
  roles: string[];
  isActive: boolean;
}

export interface UserRolesResponse {
  userId: string;
  ouMemberships: string[];
  accessGroups: UserAccessGroupInfo[];
  roles: string[];
}

interface RawUserAccessGroup {
  id: string;
  entra_object_id: string;
  display_name: string;
  description?: string | null;
  roles: string[];
  is_active: boolean;
}

interface RawUserRolesResponse {
  user_id: string;
  ou_memberships: string[];
  access_groups: RawUserAccessGroup[];
  roles: string[];
}

export const getUserRoles = async (id: string): Promise<UserRolesResponse> => {
  const response = await client.get<RawUserRolesResponse>(`/users/${id}/roles`);
  const d = response.data;
  return {
    userId: d.user_id,
    ouMemberships: d.ou_memberships || [],
    accessGroups: (d.access_groups || []).map((g) => ({
      id: g.id,
      entraObjectId: g.entra_object_id,
      displayName: g.display_name,
      description: g.description ?? undefined,
      roles: g.roles || [],
      isActive: Boolean(g.is_active),
    })),
    roles: d.roles || [],
  };
};

// WI #323: the read paths above map, but these two write paths did not — they
// posted a camelCase `Partial<User>` straight at a backend that binds
// snake_case, and typed the snake_case response as camelCase `User`. Both
// directions were silently wrong in the very module CLAUDE.md holds up as the
// worked example of convention 1: `UserCreate` would have rejected the body
// for a missing `display_name`/`email`, and `UserUpdate` — where every field is
// optional — would have accepted it and changed nothing at all.
//
// Only the fields the backend actually binds are sent. `UserCreate` takes
// display_name, email, job_title, department and entra_oid; `UserUpdate` takes
// display_name, job_title and department. Anything else in the body is ignored
// by FastAPI, so sending it would only invite the same confusion back.
const toRawUser = (data: Partial<User>): Record<string, unknown> => {
  const raw: Record<string, unknown> = {};
  if (data.displayName !== undefined) raw.display_name = data.displayName;
  if (data.email !== undefined) raw.email = data.email;
  if (data.jobTitle !== undefined) raw.job_title = data.jobTitle;
  if (data.department !== undefined) raw.department = data.department;
  if (data.entraOid !== undefined) raw.entra_oid = data.entraOid;
  return raw;
};

export const createUser = async (data: Partial<User>): Promise<User> => {
  const response = await client.post<RawUser>('/users', toRawUser(data));
  return mapUser(response.data);
};

export const updateUser = async (id: string, data: Partial<User>): Promise<User> => {
  const response = await client.put<RawUser>(`/users/${id}`, toRawUser(data));
  return mapUser(response.data);
};

// Deactivation is a soft-delete on the backend — the DELETE /users/{id}
// endpoint flips is_active=false, records an audit entry and requires
// platform_admin. Using PATCH with {isActive:false} never worked because the
// backend expects snake_case and the update path 404s on already-inactive
// users anyway. (Bug #143)
export const deactivateUser = async (id: string): Promise<void> => {
  await client.delete(`/users/${id}`);
};

export const syncUsers = async (): Promise<{ synced: number; errors: string[] }> => {
  const response = await client.post<{ synced: number; errors: string[] }>('/users/sync', {});
  return response.data;
};
